import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import ModuleCard from "../../components/modules/ModuleCard";
import { Plus, Layers, ChevronsDownUp, ChevronsUpDown, Eye, EyeOff, Tags, X } from "lucide-react";

function filterHidden(modules) {
  return modules
    .filter((m) => !m.hidden)
    .map((m) => ({
      ...m,
      sections: m.sections.filter((s) => !s.hidden).map((s) => ({ ...s, items: s.items.filter((i) => !i.hidden) })),
    }));
}

function SkillTagManager({ courseId, tags, onClose, onSaved, onError }) {
  const [list, setList] = useState(tags);
  const [draft, setDraft] = useState("");
  const save = async () => {
    try {
      await api.put(`/courses/${courseId}/skill-tags`, { tags: list });
      onSaved();
      onClose();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const add = (e) => {
    e.preventDefault();
    const t = draft.trim();
    if (t && !list.includes(t)) setList((p) => [...p, t]);
    setDraft("");
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center px-4" onClick={onClose}>
      <div data-testid="skill-tag-manager" className="nb-card-static p-6 w-full max-w-md animate-pop space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Skill tags</h3>
          <button onClick={onClose} className="w-9 h-9 border-2 border-[var(--gold)] rounded-lg flex items-center justify-center hover:bg-[var(--yellow)]"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-[var(--muted-text)]">Course-wide tags you can attach to items (e.g. Critical Thinking, Problem Solving, Communication, Technology).</p>
        <form onSubmit={add} className="flex gap-2">
          <input data-testid="skill-tag-input" value={draft} onChange={(e) => setDraft(e.target.value)} className="nb-input text-sm" placeholder="New tag…" />
          <button data-testid="skill-tag-add" className="nb-btn nb-btn-white text-sm"><Plus className="w-4 h-4" /></button>
        </form>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {list.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border-2 border-[var(--gold)]/60 text-[var(--gold-soft)]">
              {t}
              <button data-testid={`skill-tag-remove-${t}`} onClick={() => setList((p) => p.filter((x) => x !== t))} className="hover:text-red-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {list.length === 0 && <span className="text-xs text-[var(--muted-text)]">No tags yet.</span>}
        </div>
        <button data-testid="skill-tag-save" onClick={save} className="nb-btn nb-btn-yellow w-full">Save tags</button>
      </div>
    </div>
  );
}

export default function ModulesTab({ courseId, onGoToExams, onGoToProjects }) {
  const { user } = useAuth();
  const isInstructor = user?.role === "instructor";
  const [modules, setModules] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [exams, setExams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skillTags, setSkillTags] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openMap, setOpenMap] = useState({});       // moduleId/sectionId -> bool
  const [viewHidden, setViewHidden] = useState(true);
  const [tagFilter, setTagFilter] = useState("");
  const [managingTags, setManagingTags] = useState(false);

  const load = useCallback(async () => {
    try {
      const reqs = [api.get(`/courses/${courseId}/modules`), api.get(`/courses/${courseId}/skill-tags`)];
      if (isInstructor) {
        reqs.push(api.get(`/courses/${courseId}/materials`), api.get(`/courses/${courseId}/exams`), api.get(`/courses/${courseId}/projects`));
      }
      const [m, t, mats, xs, ps] = await Promise.all(reqs);
      setModules(m.data);
      setSkillTags(t.data.tags || []);
      if (isInstructor) {
        setMaterials(mats.data); setExams(xs.data); setProjects(ps.data);
      }
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [courseId, isInstructor]);

  useEffect(() => { load(); }, [load]);

  const isOpen = (id, dflt) => (openMap[id] !== undefined ? openMap[id] : dflt);
  const toggle = (id, dflt) => setOpenMap((p) => ({ ...p, [id]: !(p[id] !== undefined ? p[id] : dflt) }));
  const setAll = (value) => {
    const next = {};
    for (const m of modules) {
      next[m.id] = value;
      for (const s of m.sections || []) next[s.id] = value;
    }
    setOpenMap(next);
  };
  const expandAll = () => setAll(true);
  const collapseAll = () => setAll(false);

  const createModule = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/courses/${courseId}/modules`, form);
      setForm({ title: "", description: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const moveModule = async (index, dir) => {
    const a = shown[index]?.id, b = shown[index + dir]?.id;
    if (!a || !b) return;
    const ids = modules.map((m) => m.id);
    const ia = ids.indexOf(a), ib = ids.indexOf(b);
    [ids[ia], ids[ib]] = [ids[ib], ids[ia]];
    try { await api.post(`/courses/${courseId}/modules/reorder`, { module_ids: ids }); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const shown = isInstructor && !viewHidden ? filterHidden(modules) : modules;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="expand-all-btn" onClick={expandAll} className="nb-btn nb-btn-white text-xs flex items-center gap-1.5">
          <ChevronsUpDown className="w-3.5 h-3.5" /> Expand all
        </button>
        <button data-testid="collapse-all-btn" onClick={collapseAll} className="nb-btn nb-btn-white text-xs flex items-center gap-1.5">
          <ChevronsDownUp className="w-3.5 h-3.5" /> Collapse all
        </button>
        {skillTags.length > 0 && (
          <select data-testid="tag-filter-select" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="nb-input text-xs py-1.5 w-auto">
            <option value="">All skills</option>
            {skillTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {isInstructor && (
          <>
            <button
              data-testid="view-hidden-toggle"
              onClick={() => setViewHidden((v) => !v)}
              className={`nb-btn text-xs flex items-center gap-1.5 ${viewHidden ? "nb-btn-yellow" : "nb-btn-white"}`}
              title={viewHidden ? "Hidden modules/sections/items are shown (dimmed). Click to hide them." : "Hidden content is filtered out, like students see it. Click to show."}
            >
              {viewHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} View hidden
            </button>
            <button data-testid="manage-tags-btn" onClick={() => setManagingTags(true)} className="nb-btn nb-btn-white text-xs flex items-center gap-1.5">
              <Tags className="w-3.5 h-3.5" /> Skill tags
            </button>
          </>
        )}
      </div>

      {/* Create module */}
      {isInstructor && (
        <form onSubmit={createModule} className="nb-card-static p-5 space-y-3" data-testid="create-module-form">
          <h3 className="font-display text-xl font-bold">Add a module (chapter)</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="module-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="nb-input" placeholder='e.g. "Chapter 01: Introduction to Systems Analysis & Design"' />
            <input data-testid="module-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="nb-input" placeholder="Short description (optional)" />
          </div>
          <p className="text-xs text-[var(--muted-text)]">New modules start with three sections: Learn It, Study It, Apply It — rename or add your own.</p>
          <button data-testid="module-create-btn" disabled={busy} className="nb-btn nb-btn-yellow flex items-center gap-2">
            <Plus className="w-4 h-4" /> {busy ? "Creating…" : "Create module"}
          </button>
        </form>
      )}

      {error && <div data-testid="modules-error" className="text-sm text-red-600">{error}</div>}

      {shown.length === 0 && (
        <div className="nb-card-static p-8 text-center">
          <Layers className="w-8 h-8 mx-auto mb-2 text-[var(--gold)]" />
          <p className="text-sm text-[var(--muted-text)]">
            {isInstructor ? "No modules yet. Create one per textbook chapter, then fill its sections with readings, videos, flashcards, quizzes and assignments." : "No modules posted yet."}
          </p>
        </div>
      )}

      {shown.map((mod, idx) => (
        <ModuleCard
          key={mod.id} module={mod} index={idx} total={shown.length}
          isInstructor={isInstructor}
          open={isOpen(mod.id, idx === 0)} onToggle={() => toggle(mod.id, idx === 0)}
          isSectionOpen={(sid) => isOpen(sid, true)} onToggleSection={(sid) => toggle(sid, true)}
          onChanged={load} onError={setError} onMove={moveModule}
          onGoToExams={onGoToExams} onGoToProjects={onGoToProjects}
          materials={materials} exams={exams} projects={projects} skillTags={skillTags} tagFilter={tagFilter || null}
        />
      ))}

      {managingTags && (
        <SkillTagManager courseId={courseId} tags={skillTags} onClose={() => setManagingTags(false)} onSaved={load} onError={setError} />
      )}
    </div>
  );
}
